import {
  isInvalidVendorName,
  isInvalidVendorLocation,
  isInvalidEmail,
  isInvalidEmailGreeting,
  isInvalidShipmentField
} from "src/validation/stringValidation";
import { FormValidator } from "../formValidator";
import { ValidationError, ValidationErrors } from "src/errors";
import { CreateVendorFormDataKey } from "src/types";
import { COUNTRY_OF_EXPORT_OPTIONS, COLLECTION_TYPE_OPTIONS } from "src/constants";
import { CollectionType } from "src/enums/collectionType";
import { Incoterms } from "src/enums/incoterms";

class CreateVendorFormDataValidator extends FormValidator {
  private static validateVendorName(value: unknown): ValidationError<"vendorName"> | undefined {
    return isInvalidVendorName(value) ? new ValidationError("Enter a valid vendor name", "vendorName") : undefined;
  }

  private static validateLocation(value: unknown): ValidationError<"location"> | undefined {
    return isInvalidVendorLocation(value)
      ? new ValidationError("Enter a valid location ('United Kingdom', 'United States' or 'Germany')", "location")
      : undefined;
  }

  private static validateEmails(value: unknown): ValidationError<"emails"> | undefined {
    if (!Array.isArray(value) || !value.length || (value.length === 1 && value[0] === "")) {
      return;
    }

    for (const arrayValue of value) {
      if (isInvalidEmail(arrayValue)) {
        return new ValidationError("An email is invalid", "emails");
      }
    }

    return undefined;
  }

  private static validateEmailGreeting(value: unknown): ValidationError<"emailGreeting"> | undefined {
    return value && isInvalidEmailGreeting(value)
      ? new ValidationError("Enter a valid email greeting", "emailGreeting")
      : undefined;
  }

  private static validateIncoTerms(value: unknown): ValidationError<"incoTerms"> | undefined {
    if (!value || (typeof value === "string" && value.trim() === "")) {
      return new ValidationError("Inco Terms is required", "incoTerms");
    }
    const validIncoTerms = Object.values(Incoterms);
    if (typeof value === "string" && !validIncoTerms.includes(value as Incoterms)) {
      return new ValidationError("Enter a valid Inco Terms value", "incoTerms");
    }
    return undefined;
  }

  private static validateCountryOfExport(value: unknown): ValidationError<"countryOfExport"> | undefined {
    if (!value) {
      return undefined;
    }
    if (!Array.isArray(value)) {
      return new ValidationError("Country of Export must be an array", "countryOfExport");
    }
    if (value.length === 0) {
      return undefined; // Empty array is valid
    }
    const validCountryCodes = COUNTRY_OF_EXPORT_OPTIONS.map((option) => option.value);
    for (const countryCode of value) {
      if (typeof countryCode !== "string" || !validCountryCodes.includes(countryCode)) {
        return new ValidationError("Enter valid country codes for Country of Export", "countryOfExport");
      }
    }
    return undefined;
  }

  private static validateCollectionType(
    value: unknown,
    collectionOrderVendor: boolean
  ): ValidationError<"collectionType"> | undefined {
    if (!value || typeof value !== "string") {
      return new ValidationError("Collection type is required", "collectionType");
    }
    if (!COLLECTION_TYPE_OPTIONS.includes(value as CollectionType)) {
      return new ValidationError("Enter a valid collection type", "collectionType");
    }
    // If collectionOrderVendor is false, collectionType must be "Brand Ships"
    if (!collectionOrderVendor && value !== CollectionType.BRAND_SHIPS) {
      return new ValidationError(
        "Collection type must be 'Brand Ships' when collection vendor is 'No'",
        "collectionType"
      );
    }
    return undefined;
  }

  private static validateShipmentDetails(value: unknown): ValidationError<"shipmentDetails"> | undefined {
    if (!value || typeof value !== "object") {
      return undefined;
    }

    const shipmentDetails = value as any;

    const hasAnyValue =
      shipmentDetails.opsPOC?.trim() ||
      shipmentDetails.notes?.trim() ||
      shipmentDetails.ein?.trim() ||
      shipmentDetails.collection?.trim() ||
      shipmentDetails.contactPerson?.trim() ||
      shipmentDetails.contactInfo?.trim() ||
      shipmentDetails.email?.trim() ||
      (shipmentDetails.addresses && shipmentDetails.addresses.length > 0);

    if (!hasAnyValue) {
      return undefined;
    }
    if (!shipmentDetails.opsPOC?.trim()) {
      return new ValidationError("Ops POC is required in Shipment Details", "shipmentDetails");
    }

    if (isInvalidShipmentField(shipmentDetails.opsPOC, 50)) {
      return new ValidationError("Ops POC must be 50 characters or less in Shipment Details", "shipmentDetails");
    }

    if (shipmentDetails.notes && isInvalidShipmentField(shipmentDetails.notes, 200)) {
      return new ValidationError("Notes must be 200 characters or less in Shipment Details", "shipmentDetails");
    }

    if (shipmentDetails.collection && isInvalidShipmentField(shipmentDetails.collection, 50)) {
      return new ValidationError("Collection must be 50 characters or less in Shipment Details", "shipmentDetails");
    }

    if (!shipmentDetails.contactPerson?.trim()) {
      return new ValidationError("Contact Person Name is required in Shipment Details", "shipmentDetails");
    }

    if (isInvalidShipmentField(shipmentDetails.contactPerson, 50)) {
      return new ValidationError("Contact Person must be 50 characters or less in Shipment Details", "shipmentDetails");
    }

    if (!shipmentDetails.email?.trim()) {
      return new ValidationError("Contact email is required in Shipment Details", "shipmentDetails");
    }

    if (isInvalidEmail(shipmentDetails.email)) {
      return new ValidationError("Contact email is invalid in Shipment Details", "shipmentDetails");
    }

    if (!shipmentDetails.contactInfo?.trim()) {
      return new ValidationError("Contact phone number is required in Shipment Details", "shipmentDetails");
    }

    if (isInvalidShipmentField(shipmentDetails.contactInfo, 50)) {
      return new ValidationError(
        "Contact phone number must be 50 characters or less in Shipment Details",
        "shipmentDetails"
      );
    }

    if (!shipmentDetails.addresses || shipmentDetails.addresses.length === 0) {
      return new ValidationError("At least one address is required in Shipment Details", "shipmentDetails");
    }

    for (let i = 0; i < shipmentDetails.addresses.length; i++) {
      const address = shipmentDetails.addresses[i];

      if (!address.line1?.trim()) {
        return new ValidationError(`Address ${i + 1} line 1 is required in Shipment Details`, "shipmentDetails");
      }

      if (isInvalidShipmentField(address.line1, 200)) {
        return new ValidationError(
          `Address ${i + 1} line 1 must be 200 characters or less in Shipment Details`,
          "shipmentDetails"
        );
      }

      if (address.line2 && isInvalidShipmentField(address.line2, 200)) {
        return new ValidationError(
          `Address ${i + 1} line 2 must be 200 characters or less in Shipment Details`,
          "shipmentDetails"
        );
      }

      if (address.line3 && isInvalidShipmentField(address.line3, 200)) {
        return new ValidationError(
          `Address ${i + 1} line 3 must be 200 characters or less in Shipment Details`,
          "shipmentDetails"
        );
      }

      if (!address.city?.trim()) {
        return new ValidationError(`Address ${i + 1} city is required in Shipment Details`, "shipmentDetails");
      }

      if (!address.country?.trim()) {
        return new ValidationError(`Address ${i + 1} country is required in Shipment Details`, "shipmentDetails");
      }

      if (!address.zipCode?.trim()) {
        return new ValidationError(`Address ${i + 1} zip code is required in Shipment Details`, "shipmentDetails");
      }

      if (!address.countryCode?.trim()) {
        return new ValidationError(`Address ${i + 1} country code is required in Shipment Details`, "shipmentDetails");
      }

      if (address.countryCode.length !== 2) {
        return new ValidationError(
          `Address ${i + 1} country code must be exactly 2 characters in Shipment Details`,
          "shipmentDetails"
        );
      }
    }

    const hasCurrentAddress = shipmentDetails.addresses.some((addr: any) => addr.isCurrent);
    if (!hasCurrentAddress) {
      return new ValidationError(
        "At least one address must be marked as current in Shipment Details",
        "shipmentDetails"
      );
    }

    return undefined;
  }

  static validateShipmentDetailField(
    fieldName: keyof import("src/types/database/vendor").VendorShipmentDetails,
    value: unknown
  ): ValidationError<string> | undefined {
    switch (fieldName) {
      case "opsPOC":
        if (!value || typeof value !== "string" || !value.trim()) {
          return new ValidationError("Ops POC is required", fieldName);
        }
        return isInvalidShipmentField(value, 50)
          ? new ValidationError("Ops POC must be 50 characters or less", fieldName)
          : undefined;
      case "notes":
        return value && isInvalidShipmentField(value, 200)
          ? new ValidationError("Notes must be 200 characters or less", fieldName)
          : undefined;
      case "ein":
        // EIN is optional and can be any string (no format validation)
        return undefined;
      case "collection":
        return value && isInvalidShipmentField(value, 50)
          ? new ValidationError("Collection must be 50 characters or less", fieldName)
          : undefined;
      case "addresses":
        // Address validation is handled in the main validateShipmentDetails method
        return undefined;
      case "contactPerson":
        if (!value || typeof value !== "string" || !value.trim()) {
          return new ValidationError("Contact Person Name is required", fieldName);
        }
        return isInvalidShipmentField(value, 50)
          ? new ValidationError("Contact Person must be 50 characters or less", fieldName)
          : undefined;
      case "contactInfo":
        if (!value || typeof value !== "string" || !value.trim()) {
          return new ValidationError("Contact phone number is required", fieldName);
        }
        return isInvalidShipmentField(value, 50)
          ? new ValidationError("Contact phone number must be 50 characters or less", fieldName)
          : undefined;
      case "email":
        if (!value || typeof value !== "string" || !value.trim()) {
          return new ValidationError("Contact email is required", fieldName);
        }
        return isInvalidEmail(value) ? new ValidationError("Contact email is invalid", fieldName) : undefined;
      default:
        return undefined;
    }
  }

  private static readonly validationStrategies: Record<
    CreateVendorFormDataKey,
    (value: unknown) => ValidationError<CreateVendorFormDataKey> | undefined
  > = {
    vendorName: CreateVendorFormDataValidator.validateVendorName,
    location: CreateVendorFormDataValidator.validateLocation,
    emails: CreateVendorFormDataValidator.validateEmails,
    emailGreeting: CreateVendorFormDataValidator.validateEmailGreeting,
    portalOrderVendor: () => undefined,
    collectionOrderVendor: () => undefined,
    collectionType: () => undefined, // Will be validated separately with context
    isProForma: () => undefined,
    portalAccess: () => undefined,
    fileType: () => undefined,
    invoiceName: () => undefined,
    paymentTerms: () => undefined,
    incoTerms: CreateVendorFormDataValidator.validateIncoTerms,
    countryOfExport: CreateVendorFormDataValidator.validateCountryOfExport,
    shipmentDetails: CreateVendorFormDataValidator.validateShipmentDetails,
    isDirectToAA: () => undefined
  };

  static validateVendorFormData<T extends CreateVendorFormDataKey>(
    formData: Partial<Record<T, unknown>>
  ): ValidationErrors<T> {
    const errors = super.validateFormData(
      formData,
      CreateVendorFormDataValidator.validationStrategies
    ) as ValidationErrors<T>;

    // Validate collectionType with context of collectionOrderVendor
    const collectionType = formData["collectionType" as T] as string | undefined;
    const collectionOrderVendor = formData["collectionOrderVendor" as T] as boolean | undefined;
    if (collectionType !== undefined) {
      const collectionTypeError = CreateVendorFormDataValidator.validateCollectionType(
        collectionType,
        collectionOrderVendor ?? false
      );
      if (collectionTypeError) {
        errors.push(collectionTypeError as ValidationError<T>);
      }
    }

    return errors;
  }
}

export { CreateVendorFormDataValidator };
